import _ from "lodash";
import moment from "moment";
import momentTZ from "moment-timezone";
import {
  DEFAULT_TIMEZONE,
  ROLE_ACCESSIBILITY,
  serviceBenefitsMapping,
  SMS_TEMPLATES_ORDER_ARRAY,
  STATES,
} from "./constants";

// Short zone name for the tenant zone, e.g. "AEST" - and "AEDT" for the five
// months a daylight-saving zone is on it, which is why it is derived per call
// rather than stored.
export const tenantZoneAbbreviation = (at = momentTZ()) => momentTZ(at).tz(DEFAULT_TIMEZONE).format("z");

/**
 * Renders a stored date-time in the tenant zone.
 *
 * Previously this rendered in whatever zone the browser happened to be in, so
 * two coordinators in different states saw the same appointment an hour apart
 * with nothing on screen to say so. Date-only values (`withTime` false, used
 * for dates of birth) are left as they were: they carry no instant to convert.
 *
 * `withZone` appends the zone abbreviation and is on by default for any value
 * with a time in it; the two callers that pass the result on as data rather
 * than showing it turn it off explicitly.
 */
export const formatDateTime = (dateTimeStr, withTime = true, withWeek = false, withZone = true) => {
  if (_.isEmpty(dateTimeStr)) {
    return "";
  }
  let format = "DD/MM/YYYY";
  if (withTime) {
    format += " HH:mm";
  }
  if (withWeek) {
    format += " [W]WW";
  }
  if (!withTime) {
    return moment(dateTimeStr, "YYYY-MM-DDTHH:mm:ss Z").format(format);
  }
  const m = momentTZ(dateTimeStr, "YYYY-MM-DDTHH:mm:ss Z").tz(DEFAULT_TIMEZONE);
  return withZone ? `${m.format(format)} ${m.format("z")}` : m.format(format);
};

export const timestampToDateTime = (timestamp) => {
  if (_.isNull(timestamp)) {
    return "";
  }
  return momentTZ.unix(timestamp).tz(DEFAULT_TIMEZONE).format("YYYY-MM-DD HH:mm:ss z");
};

export const noteToServiceItems = (note) => {
  const serviceIds = Object.keys(serviceBenefitsMapping);
  for (let i = 0; i < serviceIds.length; i++) {
    if (note.indexOf(serviceIds[i]) !== -1) {
      return serviceBenefitsMapping[serviceIds[i]];
    }
  }
  return { id: "", benefit: "", extraId: "", extraBenefit: "" };
};

export const transformAppointmentList = (data) => {
  return _.reduce(
    data,
    (list, obj) => {
      list[obj.appointmentId] = obj;
      return list;
    },
    {}
  );
};

export const transformAppointment = (data) => {
  return {
    id: _.get(data, "id", ""),
    patientInitial: _.get(data, "patient.initial", ""),
    patientFirstName: _.get(data, "patient.firstName", ""),
    patientSurname: _.get(data, "patient.surname"),
    patientAddress: _.get(data, "patient.address", ""),
    patientDateOfBirth: formatDateTime(_.get(data, "patient.dateOfBirth", ""), false),
    patientEmail: _.get(data, "patient.email", ""),
    practitionerFirstName: _.get(data, "practitioner.firstName", ""),
    practitionerLastName: _.get(data, "practitioner.lastName", ""),
    clinicName: _.get(data, "clinicLocations[0].name", ""),
    dateOfService: formatDateTime(_.get(data, "dateOfService", ""), true, false, false),
    serviceItems: noteToServiceItems(_.get(data, "notes", "")),
    referenceNumber: _.get(data, "referenceNumber", ""),
    practitionerEmail: _.get(data, "practitioner.email", ""),
    referrerEmail: _.get(data, "referrerEmails", []).join(", "),
    referrerFullName: `${_.get(data, "referrers[0].firstName", "")} ${_.get(data, "referrers[0].lastName", "")}`,
    typeId: _.get(data, "typeId", ""),
    specialties: _.get(data, "practitioner.specialties", []),
    urn: _.get(data, "urn", ""),
    hospital: _.get(data, "hospital", ""),
    speciality: _.get(data, "speciality", ""),
  };
};

export const transformPatient = (data) => {
  console.log(data);
  return {
    dateOfBirth: formatDateTime(_.get(data, "client.dateOfBirth", ""), false),
    phone:
      _.get(data, "client.phoneNumberMobile", false) ||
      _.get(data, "client.phoneNumberHome", false) ||
      _.get(data, "client.phoneNumberWork", false) ||
      "",
    email: _.get(data, "client.email", ""),
  };
};

export const isInvalidDate = (dateStr) => {
  const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const v = datePattern.exec(dateStr);
  return _.isNull(v) || !moment(dateStr, "YYYY-MM-DD").isValid();
};

export const isInvalidDateInAvailableDates = (dateStr, availableDates) => {
  const datePattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const v = datePattern.exec(dateStr);
  return _.isNull(v) || !moment(dateStr, "YYYY-MM-DD").isValid() || !availableDates?.includes(dateStr);
};

export const isInvalidTime = ({ timeStr, availableTimes, duration }) => {
  let timePattern = /^(\d{2}):(\d{2})$/;
  const timeFormat = "HH:mm";
  const endTime = moment(timeStr, timeFormat).add(duration, "minutes").format(timeFormat);
  const timeslot = availableTimes.find(({ start, end }) => timeStr >= start && endTime <= end);

  const v = timePattern.exec(timeStr);
  return _.isNull(v) || !moment(`1977-02-22 ${timeStr}`, "YYYY-MM-DD HH:mm").isValid() || !timeslot;
};

export const isValidEmail = (email) => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const transformTrackedAppointment = (data) => {
  const patientName = `${_.get(data, "client.firstName", "")} ${_.get(data, "client.lastName", "")}`;
  const dateOfService = `${formatDateTime(_.get(data, "appointment.startDateTime", ""), true, false, false)}`;
  const referrers = _.get(data, "referrers", []).map((referrer) => `${referrer.firstName} ${referrer.lastName}`);
  const referrerNames = referrers.length > 0 ? referrers.join(", ") : "";
  const trackedAppointment = _.get(data, "trackedAppointment", null);

  return { patientName, dateOfService, referrerNames, trackedAppointment };
};

export const getStartTimeOptions = () => {
  let options = [];
  let count = 0;
  while (count <= 186) {
    const mt = moment("07:00", "hh:mm").add(count * 5, "m");
    options.push({
      label: mt.format("hh:mm A"),
      value: mt.format("HH:mm"),
    });
    count++;
  }
  return options;
};

export const getDurationOptions = () => {
  let options = [];
  for (let i = 5; i <= 60; i += 5) {
    options.push({
      label: `${i} mins`,
      value: i,
    });
  }
  for (let i = 90; i <= 120; i += 30) {
    options.push({
      label: `${i} mins`,
      value: i,
    });
  }
  return options;
};

// export const getValidMobilePhoneNumber = (mobileNumber) => {
//   if (_.isNull(mobileNumber)) {
//     return null;
//   }
//
//   // remove all non-digit characters
//   let result = mobileNumber.replace(/\D/g, "");
//
//   if (result.length < 8) {
//     return null;
//   }
//
//   // get last 8 digits
//   result = result.slice(-8);
//
//   return `+614${result}`;
// };

export const generateAgeByDOB = (clientDOB, format) => {
  const DOBMoment = moment(clientDOB, format);
  return moment().diff(DOBMoment, "years", false);
};

export const smsTemplatesSorter = (template1, template2) => {
  if (SMS_TEMPLATES_ORDER_ARRAY.includes(template1.name) && SMS_TEMPLATES_ORDER_ARRAY.includes(template2.name)) {
    return SMS_TEMPLATES_ORDER_ARRAY.indexOf(template1.name) - SMS_TEMPLATES_ORDER_ARRAY.indexOf(template2.name);
  }
  if (SMS_TEMPLATES_ORDER_ARRAY.includes(template2.name)) {
    return 1;
  }
  if (SMS_TEMPLATES_ORDER_ARRAY.includes(template1.name)) {
    return -1;
  }
  return template1.name < template2.name ? -1 : 1;
};

export const splitSuburbStatePostcodeString = (suburbStatePostalCode) => {
  const addressList = suburbStatePostalCode?.split(" ");
  if (addressList?.length > 0) {
    const postcode = addressList.pop();
    const state = addressList.pop();
    const suburb = addressList.join(" ");

    const validation = {
      isPostcode: () => {
        const pattern = /^\d{4}$/;
        return pattern.test(postcode);
      },
      isState: () => {
        const stateUpperCase = state.toUpperCase();
        return STATES.includes(stateUpperCase);
      },
    };

    if (validation.isPostcode() && validation.isState()) {
      return { postcode, state, suburb };
    }
  }
};

export const isPermit = (role, scope) => {
  return ROLE_ACCESSIBILITY[role] && ROLE_ACCESSIBILITY[role].includes(scope);
};

export const getUsersWithViewableTodos = (currentUser, users, scope) => {
  if (!currentUser) {
    return [];
  }
  const sharedUsers = users.filter((user) => user.shared);
  return isPermit(currentUser?.role, scope) ? users : [currentUser, ...sharedUsers];
};
